import type { AssistantSurface } from '@luxuryescapes/contract-trip'
import { trackClientEvent } from 'analytics/analytics'
import type { SurfaceFocusOutcome } from 'tripPlanner/assistant/surfaceFocus/surfaceFocusTypes'

const CATEGORY = 'chat_trip_planner'

export type EntryPointType =
  | 'typed_prompt'
  | 'recent_search_card'
  | 'booking_card'
  | 'suggestion_chip'
  | 'recent_trip_card'
  | 'viewed_offer_card'

function chatSubject(descriptor: string, chatId: number | undefined): string {
  return chatId !== undefined ? `${descriptor}_${chatId}` : descriptor
}

export function trackSessionStart(
  chatId: number | undefined,
  sessionType: 'new' | 'returning',
) {
  trackClientEvent({
    subject: chatSubject(`session_${sessionType}`, chatId),
    action: 'session_start',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackEntryPoint(entryType: EntryPointType) {
  trackClientEvent({
    subject: `entry_${entryType}`,
    action: 'entry_point_selected',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackFirstMessageSent(chatId: number, timeToFirstMessageMs: number) {
  trackClientEvent({
    subject: chatSubject(`first_message_${Math.round(timeToFirstMessageMs)}ms`, chatId),
    action: 'first_message_sent',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackHotelDrawerOpened(chatId: number) {
  trackClientEvent({
    subject: chatSubject('hotel_drawer', chatId),
    action: 'hotel_drawer_opened',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackExperienceDrawerOpened(chatId: number) {
  trackClientEvent({
    subject: chatSubject('experience_drawer', chatId),
    action: 'experience_drawer_opened',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackCruiseDrawerOpened(chatId: number) {
  trackClientEvent({
    subject: chatSubject('cruise_drawer', chatId),
    action: 'cruise_drawer_opened',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackVoiceInputUsed(chatId: number | undefined) {
  trackClientEvent({
    subject: chatSubject('voice_input', chatId),
    action: 'voice_input_used',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackMapPinClicked(chatId: number, offerId: string) {
  trackClientEvent({
    subject: chatSubject(`map_pin_${offerId}`, chatId),
    action: 'map_pin_clicked',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackViewModeToggled(chatId: number, newMode: 'detail' | 'compact') {
  trackClientEvent({
    subject: chatSubject(`view_mode_${newMode}`, chatId),
    action: 'view_mode_toggled',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackCarouselPaginated(chatId: number | undefined) {
  trackClientEvent({
    subject: chatSubject('carousel', chatId),
    action: 'carousel_paginated',
    category: CATEGORY,
    type: 'interaction',
  })
}

export function trackSessionEnd(chatId: number | undefined, durationMs: number) {
  trackClientEvent({
    subject: chatSubject(`session_end_${Math.round(durationMs)}ms`, chatId),
    action: 'session_end',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export type AssistantSurfaceSnapshotOutcome =
  | 'chunked_resolved'
  | 'chunked_recovery_resolved'
  | 'degraded_timeout'
  | 'degraded_chunk_cache_miss'
  | 'degraded_validation'
  | 'degraded_network'
  | 'degraded_auth'
  | 'degraded_idempotency'

export type AssistantSurfaceSnapshotLatencyBucket =
  | 'under_100ms'
  | '100_to_249ms'
  | '250_to_499ms'
  | '500_to_749ms'
  | 'deadline'

export function assistantSurfaceSnapshotLatencyBucket(elapsedMs: number): AssistantSurfaceSnapshotLatencyBucket {
  if (elapsedMs < 100) return 'under_100ms'
  if (elapsedMs < 250) return '100_to_249ms'
  if (elapsedMs < 500) return '250_to_499ms'
  if (elapsedMs < 750) return '500_to_749ms'
  return 'deadline'
}

export function trackAssistantSurfaceSnapshotOutcome(
  surface: string,
  outcome: AssistantSurfaceSnapshotOutcome,
  latencyBucket: AssistantSurfaceSnapshotLatencyBucket,
) {
  trackClientEvent({
    subject: `${surface}_${outcome}_${latencyBucket}`,
    action: 'assistant_surface_snapshot_resolved',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackSurfaceActivityDropped(count: number) {
  trackClientEvent({
    subject: `trip_planner_activity_dropped_${count}`,
    action: 'surface_activity_accumulator_capped',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackCommittedStateDropped(count: number) {
  trackClientEvent({
    subject: `trip_planner_committed_state_dropped_${count}`,
    action: 'committed_state_accumulator_capped',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}

export function trackOfferPageFocusExecutorOutcome(outcome: SurfaceFocusOutcome, surface: AssistantSurface = 'accommodation_offer_page') {
  trackClientEvent({
    subject: `${surface}_${outcome}`,
    action: 'offer_page_focus_executor_resolved',
    category: CATEGORY,
    type: 'nonInteraction',
  })
}
